import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as Constants from '@emi/shared/constants';
import { SkuSearchService as GetSkus } from '@emi/shared/data-access';
import { ConfigureData, SearchOption } from '@emi/shared/models';
import { DataExtractionState, addConfigureDataSelection, getExchangeRates, getIsLoadingSelections, getSelectedConfigureData } from '@emi/shared/state/filters';
import { SortOptionsByPageState, SkuSearchState, getSkuSearchResults, selectSortByRLP, selectSkuSearchState, setCurrency, setSortByResultsPage, selectOrderByRLP, setOrderByResultsPage } from '@emi/shared/state/sku-search';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { SearchFilterSummaryComponent, SearchFilterSummaryData } from '../..';
import { take } from 'rxjs/operators';


@Component({
    selector: 'via-sorting-options',
    templateUrl: './sorting-options.component.html',
    styleUrls: ['./sorting-options.component.scss'],
})
export class SortingOptionsComponent implements OnInit, OnDestroy {
    
    @Output()
    public skuCount: number;

    public isLoading: boolean;
    public searchTerm = '';
    public searchOption: SearchOption;
    public disableOptions: boolean;
    public selectedCurrency: string;
    public isFilterSelected: boolean;
    public sortByTooltip = Constants.Tooltips.SORTBY;
    public configureData = new ConfigureData();
    public includeSearchPhrase: boolean | undefined;

    public sortOptions = [
        { value: '', label: 'SKU Details' },
        { value: 'totalSkuSize', label: 'SKU Size' },
        { value: 'lastSeenPrice', label: 'Last Seen Price' },
    ];

    public selectedSortOption = '';
    public selectedOrder = '';

    private skuSearchSubscription: Subscription;
    private configureDataSubscription: Subscription;
    private readonly dataExtractionLoading$: Observable<boolean> = this.dataExtractionStore.select(getIsLoadingSelections);
    private resetSortingOptionsSubscription: Subscription;

    constructor(
        private readonly store: Store<SkuSearchState>,
        private readonly sortOptionsByPageStore: Store<SortOptionsByPageState>,
        public dataExtractionStore: Store<DataExtractionState>,
        private getSkusService: GetSkus,
        public dialog: MatDialog
    ) { }

    public ngOnInit(): void {
        this.skuSearchSubscription = this.store.pipe(select(selectSkuSearchState)).subscribe((state) => {
            this.searchTerm = state.searchTerm;
            this.skuCount = state.skuCount;
            this.searchOption = state.searchOption;
            this.disableOptions = !(state.skuCount > 0);
            this.selectedCurrency = state.currency;
            this.isLoading = state.isLoading;
            this.includeSearchPhrase = state.includeSearchPhrase
        });
        this.getSkusService.notifyNewSearchSubject.subscribe((freshSearch) => {
            this.isFilterSelected = !freshSearch;
        });
        this.configureDataSubscription = this.dataExtractionStore.pipe(select(getSelectedConfigureData)).subscribe((x) => {
            this.configureData = x;
        });
        this.dataExtractionStore.dispatch(getExchangeRates({ currency: this.selectedCurrency }));

        this.loadSortOptions();
    }

    private loadSortOptions(): void {
        this.sortOptionsByPageStore.pipe(select(selectSortByRLP), take(1)).subscribe((sortBy) => {
            this.selectedSortOption = sortBy || '';
        });

        this.sortOptionsByPageStore.pipe(select(selectOrderByRLP), take(1)).subscribe((orderBy) => {
            this.selectedOrder = orderBy || '';
        });
    }

    public updateCurrency(currency: string) {
        this.store.dispatch(setCurrency({ currency: currency }));
        this.dataExtractionStore.dispatch(getExchangeRates({ currency }));
        this.store.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: {
                    label: this.searchOption.label,
                    value: this.searchOption.value,
                    searchDimension: this.searchOption.label
                },
                includeSearchPhrase: this.includeSearchPhrase
            })
        );
        const configureData = { ...this.configureData, currency: currency };

        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
        this.dataExtractionStore.dispatch(getExchangeRates({ currency }));
    }

    public onSortByChange() {
        this.sortOptionsByPageStore.dispatch(setSortByResultsPage({ sortBy: this.selectedSortOption }));

        this.store.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: this.searchOption,
                sortBy: this.selectedSortOption,
                orderBy: this.selectedOrder,
                includeSearchPhrase: this.includeSearchPhrase
            })
        );
    }

    public toggleSortOrder(): void {
        this.selectedOrder === 'descending' ? (this.selectedOrder = 'ascending') : (this.selectedOrder = 'descending');
        
        this.sortOptionsByPageStore.dispatch(setOrderByResultsPage({ orderBy: this.selectedOrder }));
        
        this.store.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: this.searchOption,
                sortBy: this.selectedSortOption,
                orderBy: this.selectedOrder,
                includeSearchPhrase: this.includeSearchPhrase
            })
        );
    }

    public showSearchAndFilterSummary() {
        this.dialog.open(SearchFilterSummaryComponent, {
            data: {
                searchTerm: this.searchTerm,
                searchOptions: this.searchOption,
            } as SearchFilterSummaryData,
        });
    };

    ngOnDestroy(): void {
        this.skuSearchSubscription.unsubscribe();
    }
}
